import { useEffect, useMemo, useState, type ReactElement } from 'react';
import type { FreeSlot, Uuid } from '@zenda/api-contract';
import { Icon } from './Icon';
import { useTip } from './Tip';
import { useT } from '../lib/i18n';
import { CalendarioPopover } from './CampoData';
import { CamposDaIntegracaoForm, useCamposDaIntegracao } from './CamposDaIntegracao';
import { useAgenda, useBooking, useFreeSlots, usePatient } from '../lib/queries';
import { ParaQuem } from './ParaQuem';
import { dataDeHoje, formatDateShort } from '../lib/format';
import { slotReason } from '../lib/labels';
import { useSerieDeConsultas } from '../lib/serie';

/** Em português sempre: vai no texto para o paciente, não na tela. */
const DIA_DA_MENSAGEM = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const;
/** Horários por busca. Um dia com dois locais já produz uns 24. */
const POR_BUSCA = 200;

function diaDaSemana(data: string): number {
  const [a = '1970', m = '01', d = '01'] = data.split('-');
  return new Date(Date.UTC(Number(a), Number(m) - 1, Number(d))).getUTCDay();
}

function maisDias(data: string, dias: number): string {
  return new Date(Date.parse(`${data}T00:00:00Z`) + dias * 86_400_000).toISOString().slice(0, 10);
}

function rotulo(s: { date: string; hora: string }): string {
  return `${DIA_DA_MENSAGEM[diaDaSemana(s.date)]} ${formatDateShort(s.date)} às ${s.hora}`;
}

/**
 * A identidade de um horario inclui o LOCAL.
 *
 * O medico atende as 10:00 no consultorio e as 10:00 por teleconsulta: sao dois
 * horarios diferentes no mesmo minuto. Comparar so data e hora acendia os dois
 * ao clicar em um.
 */
function chave(s: { date: string; hora: string; locationId: string | null }): string {
  return `${s.date}|${s.hora}|${s.locationId ?? 'tele'}`;
}

/**
 * Marcar consulta enquanto fala com a paciente.
 *
 * ── Por que a preferencia vem primeiro ──
 * A conversa real comeca com "so posso de manha, terca ou quinta". A tela que
 * abre numa lista de todos os horarios obriga a atendente a filtrar de cabeca
 * enquanto a paciente espera. Aqui os filtros sao a primeira linha, e a lista
 * ja nasce respondendo.
 *
 * ── Um dia por vez ──
 * A lista corrida de varios dias empilhados nao dava lugar nenhum: para achar a
 * quinta era preciso rolar por tudo. Aqui a tela mostra UM dia, e as setas andam
 * de um dia com vaga para o proximo — pular os dias em que o medico nao atende e
 * o comportamento certo, senao numa agenda de terca e quinta a maioria dos
 * cliques cai no vazio.
 *
 * ── Por que a serie mostra os conflitos ANTES ──
 * Oito sessoes combinadas que viram cinco sao pior que nenhuma. A tela propoe
 * as oito datas, marca as ocupadas, e so entao confirma tudo de uma vez.
 */
export function Marcar({
  clientId,
  contactId,
  conversationId,
  contactName,
  doctorName,
  onMarcado,
  onFechar,
}: {
  readonly clientId: Uuid;
  readonly contactId: Uuid;
  /** Nula quando a consulta é com outro médico, que não é o desta conversa. */
  readonly conversationId: Uuid | null;
  readonly contactName: string;
  readonly doctorName: string;
  /** O texto de confirmação, para a atendente ler e enviar. */
  readonly onMarcado: (texto: string) => void;
  readonly onFechar: () => void;
}): ReactElement {
  const tip = useTip();
  const t = useT();
  const m = t.marcar;
  const nomesDosDias = t.agenda.dias;
  const [dias, setDias] = useState<readonly number[]>([]);
  const [periodo, setPeriodo] = useState<'manha' | 'tarde' | 'qualquer'>('qualquer');
  const [encaixe, setEncaixe] = useState(false);
  const [escolhido, setEscolhido] = useState<FreeSlot | null>(null);
  const [tipoId, setTipoId] = useState<string>('');
  /** Nulo = ainda vale a duracao usual daquele medico, que vem do servidor. */
  const [duracaoEscolhida, setDuracaoEscolhida] = useState<number | null>(null);
  const [titulo, setTitulo] = useState('');
  const [dependentId, setDependentId] = useState<Uuid | null>(null);
  const paciente = usePatient(clientId, contactId);
  const [limite, setLimite] = useState(POR_BUSCA);
  /** Onde a janela de busca começa. Vazio é hoje. */
  const [desde, setDesde] = useState('');
  /** O dia à vista. Vazio é o primeiro com vaga. */
  const [dia, setDia] = useState('');
  /**
   * A seta pediu um dia que ainda não veio na resposta. Guarda o pedido até a
   * busca voltar, senão o clique na borda da janela não faz nada.
   */
  const [indo, setIndo] = useState<{ para: 'antes' | 'depois'; de: string } | null>(null);

  /**
   * Os tipos vem da configuracao, e nao da busca.
   *
   * Antes eles voltavam DENTRO da resposta da busca, e a busca nao precisava saber
   * quanto a consulta dura. Agora precisa — os comecos possiveis dependem disso —, e
   * um tipo que so chega depois da primeira busca a deixaria sem duracao. Ver ADR
   * 0020.
   */
  const cfg = useAgenda(clientId);
  const tipos = cfg.data?.types ?? [];
  const tipoAtivo = tipos.find((x) => x.id === tipoId);
  /**
   * A duracao e um campo, e nao uma consequencia do tipo.
   *
   * Escolher um tipo preenche o numero, mas o numero manda: e ele que define quais
   * comecos existem. Amarrar a duracao aos tipos cadastrados deixaria de fora o caso
   * comum — a duracao que aquele medico mais usa nao e necessariamente a de um tipo. A
   * da Dra. Helena e 30 min, e os tipos dela sao 15, 20 e 40.
   *
   * `?? usual` derivado, e nao num efeito: `usualDurationMin` chega depois da primeira
   * renderizacao, e semear estado por efeito piscaria a busca com o valor errado.
   */
  const duracao = duracaoEscolhida ?? cfg.data?.usualDurationMin ?? 0;

  const busca = useFreeSlots(
    clientId,
    {
      durationMin: duracao,
      from: desde,
      weekdays: dias,
      periodo,
      limit: limite,
      overbook: encaixe,
    },
    duracao > 0,
  );
  const { marcar } = useBooking(contactId, conversationId);
  /**
   * Os campos que a plataforma exige, para o LOCAL do horario escolhido.
   *
   * Aqui o local vem depois: a atendente busca por preferencia e o horario e a
   * resposta. Enquanto nao ha horario escolhido nao ha local, e o hook devolve lista
   * vazia — que e o mesmo caminho do cliente em agenda Zenda.
   */
  const extra = useCamposDaIntegracao(clientId, escolhido?.locationId ?? null);

  const slots = busca.data?.slots ?? [];

  /** Um dia, e dentro dele um grupo por local. */
  const porDia = useMemo(() => {
    const mapa = new Map<string, Map<string, { nome: string | null; slots: FreeSlot[] }>>();
    for (const s of slots) {
      const locais = mapa.get(s.date) ?? new Map();
      const k = s.locationId ?? 'tele';
      const g = locais.get(k) ?? { nome: s.locationName, slots: [] };
      g.slots.push(s);
      locais.set(k, g);
      mapa.set(s.date, locais);
    }
    return [...mapa.entries()].map(([date, locais]) => ({ date, locais: [...locais.values()] }));
  }, [slots]);

  const idx = Math.max(
    0,
    porDia.findIndex((d) => d.date === dia),
  );
  const atual = porDia[idx];
  /** Pode haver mais dias além da janela: a busca veio cheia. */
  const talvezMais = slots.length >= limite;

  useEffect(() => {
    if (indo === null) return;
    if (busca.isFetching) return;
    const achado =
      indo.para === 'depois'
        ? porDia.find((d) => d.date > indo.de)
        : [...porDia].reverse().find((d) => d.date < indo.de);
    if (achado !== undefined) setDia(achado.date);
    setIndo(null);
  }, [indo, porDia, busca.isFetching]);

  function andar(passo: -1 | 1): void {
    const vizinho = porDia[idx + passo];
    if (vizinho !== undefined) {
      setDia(vizinho.date);
      return;
    }
    const de = atual?.date ?? dataDeHoje();
    if (passo === 1) {
      if (!talvezMais) return;
      setLimite((v) => v + POR_BUSCA);
    } else {
      const inicio = desde === '' ? dataDeHoje() : desde;
      if (inicio <= dataDeHoje()) return;
      setDesde(maisDias(inicio, -7) < dataDeHoje() ? dataDeHoje() : maisDias(inicio, -7));
      setLimite((v) => Math.max(v, POR_BUSCA + 100));
    }
    setIndo({ para: passo === 1 ? 'depois' : 'antes', de });
  }

  function irPara(data: string): void {
    if (data === '') return;
    setDesde(data);
    setDia(data);
    setLimite(POR_BUSCA);
  }

  const temAntes = idx > 0 || (desde !== '' && desde > dataDeHoje());
  const temDepois = idx < porDia.length - 1 || talvezMais;

  const {
    sessoes,
    setSessoes,
    cadaSemanas,
    setCadaSemanas,
    serie,
    conflitos,
    aMarcar,
    conferindo,
  } = useSerieDeConsultas({
    clientId,
    inicio: escolhido === null ? null : { date: escolhido.date, hora: escolhido.hora },
    duracao,
    locationId: escolhido?.locationId ?? null,
    encaixe,
  });

  function confirmar(): void {
    if (escolhido === null) return;
    const escolhidos = aMarcar;
    marcar.mutate(
      {
        contactId,
        conversationId,
        clientId,
        ...(dependentId === null ? {} : { dependentId }),
        ...(tipoAtivo === undefined ? {} : { typeId: tipoAtivo.id }),
        durationMin: duracao,
        title: titulo,
        overbook: encaixe,
        ...(extra.campos.length === 0 ? {} : { externalFields: extra.valores }),
        slots: escolhidos.map((x) => ({
          date: x.date,
          hora: x.hora,
          locationId: escolhido.locationId,
        })),
      },
      {
        onSuccess: () => {
          const onde =
            escolhido.locationName === null ? 'por teleconsulta' : `no ${escolhido.locationName}`;
          // A mensagem vai para o responsável: a consulta do filho é dita como dele.
          const dependente =
            paciente.data?.patient.dependents.find((d) => d.id === dependentId)?.name ?? null;
          const daConsulta = dependente === null ? 'sua consulta' : `a consulta de ${dependente}`;
          const asSessoes =
            dependente === null
              ? `suas ${escolhidos.length} sessões`
              : `as ${escolhidos.length} sessões de ${dependente}`;
          const texto =
            escolhidos.length === 1
              ? `Oi ${contactName}! Confirmando: ${daConsulta} com ${doctorName} ficou para ${rotulo(escolhidos[0] as { date: string; hora: string })}, ${onde}. Qualquer coisa é só me chamar por aqui.`
              : `Oi ${contactName}! Confirmando ${asSessoes} com ${doctorName}, ${onde}:\n${escolhidos.map((x) => `• ${rotulo(x)}`).join('\n')}\nQualquer coisa é só me chamar por aqui.`;
          onMarcado(texto);
          onFechar();
        },
      },
    );
  }

  function trocarEncaixe(ligado: boolean): void {
    setEncaixe(ligado);
    // O horário escolhido pode ter sumido da lista ao desligar o encaixe.
    if (!ligado && escolhido?.full === true) setEscolhido(null);
  }

  function trocarFiltro(fn: () => void): void {
    fn();
    setEscolhido(null);
    setDia('');
    setLimite(POR_BUSCA);
  }

  return (
    <div className="mk">
      <div className="mk__topo">
        <p className="mk__titulo">{m.titulo}</p>
        <button type="button" className="mk__x" onClick={onFechar} aria-label={m.fechar}>
          <Icon name="x" size={14} />
        </button>
      </div>

      {/* ── O que a paciente pode ─────────────────────────────────────────── */}
      <div className="mk__filtros">
        <div className="mk__dias" role="group" aria-label={m.diasQuePode}>
          {nomesDosDias.letra.map((d, i) => (
            <button
              key={i}
              type="button"
              className={`mk__dia${dias.includes(i) ? ' is-on' : ''}`}
              aria-pressed={dias.includes(i)}
              onClick={() =>
                trocarFiltro(() =>
                  setDias((v) => (v.includes(i) ? v.filter((x) => x !== i) : [...v, i])),
                )
              }
              {...tip(nomesDosDias.curto[i] as string)}
            >
              {d}
            </button>
          ))}
        </div>

        <div className="mk__periodo" role="group" aria-label={m.periodo}>
          {(
            [
              ['qualquer', m.periodos.qualquer],
              ['manha', m.periodos.manha],
              ['tarde', m.periodos.tarde],
            ] as const
          ).map(([v, r]) => (
            <button
              key={v}
              type="button"
              className={`mk__per${periodo === v ? ' is-on' : ''}`}
              aria-pressed={periodo === v}
              onClick={() => trocarFiltro(() => setPeriodo(v))}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="mk__duracao">
        {tipos.length === 0 ? null : (
          <select
            className="input input--sm mk__tipo"
            value={tipoAtivo?.id ?? ''}
            onChange={(e) =>
              trocarFiltro(() => {
                setTipoId(e.target.value);
                const t = tipos.find((x) => x.id === e.target.value);
                if (t !== undefined) setDuracaoEscolhida(t.durationMin);
              })
            }
            {...tip(m.dicaTipo)}
          >
            <option value="">{m.semTipo}</option>
            {tipos.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label} · {t.durationMin} min
              </option>
            ))}
          </select>
        )}
        <label className="mk__min" {...tip(m.dicaDuracao)}>
          {m.dura}
          <input
            type="number"
            className="input input--xs"
            min={5}
            max={480}
            step={5}
            value={duracao === 0 ? '' : duracao}
            onChange={(e) => {
              const v = Math.max(5, Math.min(480, Number(e.target.value) || 5));
              trocarFiltro(() => {
                setDuracaoEscolhida(v);
                setTipoId((atual) =>
                  tipos.find((x) => x.id === atual)?.durationMin === v ? atual : '',
                );
              });
            }}
          />
          min
        </label>
      </div>

      {/* ── O dia ────────────────────────────────────────────────────────── */}
      <div className="mk__nav">
        <button
          type="button"
          className="mk__seta"
          onClick={() => andar(-1)}
          disabled={!temAntes || busca.isFetching}
          aria-label={m.diaAnterior}
          {...tip(m.diaAnterior)}
        >
          <Icon name="chevron-left" size={14} />
        </button>

        {/* O rótulo É o seletor: clicar abre o calendário e pula para o dia. */}
        <CalendarioPopover
          value={atual?.date ?? ''}
          onChange={irPara}
          min={dataDeHoje()}
          alvo={(abrir, aberto) => (
            <button
              type="button"
              className={`mk__nav-dia${aberto ? ' is-open' : ''}`}
              onClick={abrir}
              {...tip(m.escolherDia)}
            >
              {atual === undefined ? (
                <span className="mk__nav-vazio">—</span>
              ) : (
                <>
                  <span>{nomesDosDias.curto[diaDaSemana(atual.date)]}</span>
                  <span className="num">{formatDateShort(atual.date)}</span>
                </>
              )}
              <Icon name="calendar" size={12} />
            </button>
          )}
        />

        <button
          type="button"
          className="mk__seta"
          onClick={() => andar(1)}
          disabled={!temDepois || busca.isFetching}
          aria-label={m.proximoDia}
          {...tip(m.proximoDia)}
        >
          <Icon name="chevron-right" size={14} />
        </button>
      </div>

      {/* ── Os horários do dia ───────────────────────────────────────────── */}
      {busca.isPending ? (
        <p className="mk__vazio">{m.procurando}</p>
      ) : atual === undefined ? (
        <p className="mk__vazio">
          {m.nenhumHorario}
          {encaixe ? '' : ` ${m.afrouxe}`}
        </p>
      ) : (
        <div className="mk__slots">
          {atual.locais.map((g) => (
            <div key={g.nome ?? 'tele'} className="mk__local">
              <p className="mk__local-nome">{g.nome ?? 'Teleconsulta'}</p>
              <div className="mk__horas">
                {g.slots.map((s) => (
                  <button
                    key={chave(s)}
                    type="button"
                    className={`mk__hora${escolhido !== null && chave(escolhido) === chave(s) ? ' is-on' : ''}${s.full ? ' is-cheio' : ''}`}
                    onClick={() => setEscolhido(s)}
                    {...(s.full
                      ? tip(
                          `${s.taken} já marcado${s.taken > 1 ? 's' : ''}. O encaixe soma mais uma pessoa, não substitui.`,
                        )
                      : {})}
                  >
                    <span className="num">{s.hora}</span>
                    {s.full ? <span className="mk__hora-cheio">+{s.taken}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* O escolhido pode estar num dia que saiu de vista ao navegar. */}
      {escolhido !== null && escolhido.date !== atual?.date ? (
        <p className="mk__escolhido">
          <span>{m.escolhido(rotulo(escolhido))}</span>
          <button type="button" onClick={() => setDia(escolhido.date)} className="mk__escolhido-ir">
            {m.ver}
          </button>
        </p>
      ) : null}

      {/* O tema da consulta, para a atendente saber do que se trata quando
          reabrir a agenda. Fica AQUI e nao na confirmacao: e anotacao interna, e
          a mensagem que vai para a paciente continua so com data, hora e local. */}
      <ParaQuem
        className="select select--sm"
        paciente={paciente.data?.patient.name ?? ''}
        dependentes={paciente.data?.patient.dependents ?? []}
        valor={dependentId}
        aoMudar={setDependentId}
      />
      <input
        className="input input--xs mk__titulo-consulta"
        value={titulo}
        placeholder={m.assunto}
        maxLength={120}
        onChange={(e) => setTitulo(e.target.value)}
        {...tip(m.assuntoDica)}
      />

      {/* ── Série ────────────────────────────────────────────────────────── */}
      {escolhido === null ? null : (
        <div className="mk__serie">
          {/* Os campos da plataforma vem antes da serie: eles decidem se a reserva
              sai, e repetir uma reserva que vai ser recusada nao ajuda ninguem. */}
          <CamposDaIntegracaoForm estado={extra} />

          <label className="mk__rep">
            {m.repetir}
            <input
              type="number"
              className="input input--xs"
              min={1}
              max={24}
              value={sessoes}
              onChange={(e) => setSessoes(Math.max(1, Math.min(24, Number(e.target.value) || 1)))}
            />
            {m.vezesACada(sessoes)}
            <input
              type="number"
              className="input input--xs"
              min={1}
              max={8}
              value={cadaSemanas}
              onChange={(e) =>
                setCadaSemanas(Math.max(1, Math.min(8, Number(e.target.value) || 1)))
              }
            />
            {m.semanas(cadaSemanas)}
          </label>

          {sessoes === 1 ? null : (
            <ul className="mk__datas">
              {serie.map((x, i) => (
                <li key={x.date} className={x.livre ? '' : 'is-conflito'}>
                  <span className="mk__datas-n">{i + 1}</span>
                  <span className="num">
                    {nomesDosDias.curto[diaDaSemana(x.date)]} {formatDateShort(x.date)} · {x.hora}
                  </span>
                  {x.livre ? null : <span className="mk__conflito">{slotReason(x.motivo)}</span>}
                </li>
              ))}
            </ul>
          )}

          {conferindo ? <p className="mk__nota">{m.conferindo}</p> : null}

          {conflitos > 0 && !encaixe ? (
            <p className="mk__aviso">
              <Icon name="alert" size={13} />
              {m.conflitos(conflitos)}
            </p>
          ) : null}
        </div>
      )}

      {marcar.isError ? (
        <p className="mk__aviso">
          <Icon name="alert" size={13} />
          {marcar.error.message}
        </p>
      ) : null}

      <div className="mk__acoes">
        <label className="mk__encaixe" {...tip(m.dicaEncaixe)}>
          <input
            type="checkbox"
            checked={encaixe}
            onChange={(e) => trocarEncaixe(e.target.checked)}
          />
          {m.encaixe}
        </label>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={confirmar}
          /* Obrigatorio faltando trava aqui: o adaptador recusaria de qualquer
             forma, e recusar depois do clique gasta a espera de quem esta ao
             telefone com a paciente. */
          disabled={escolhido === null || marcar.isPending || extra.faltando.length > 0}
        >
          {marcar.isPending
            ? m.marcando
            : escolhido === null || sessoes === 1
              ? m.marcar
              : m.marcarN(serie.filter((x) => x.livre || encaixe).length)}
        </button>
      </div>
    </div>
  );
}
